/**
 * Предпросмотр текста заявления (ТЗ FR-33) и сравнение с предыдущей
 * версией (FR-34).
 *
 * Документ собирается здесь же, из него читается текст и тут же забывается:
 * ничего не сохраняется (ТЗ п. 4.2).
 */

import { useEffect, useRef, useState } from 'react';
import type { CaseProfile } from '@bd/domain';
import type { DocumentKind } from '@bd/doc-engine';
import { api } from '../../api.js';

const DOCUMENTS: Array<[kind: DocumentKind, title: string]> = [
  ['zayavlenie', 'Заявление'],
  ['opis-imushchestva', 'Опись имущества'],
  ['spisok-kreditorov', 'Список кредиторов'],
];

export function PreviewPane({ profile }: { profile: CaseProfile }) {
  const [kind, setKind] = useState<DocumentKind>('zayavlenie');
  const [paragraphs, setParagraphs] = useState<string[]>([]);
  const [diff, setDiff] = useState<Array<{ status: string; text: string }> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previous = useRef<string[] | null>(null);

  const load = async (compare: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.preview(profile, kind, compare ? previous.current ?? undefined : undefined);
      setParagraphs(result.paragraphs);
      setDiff(result.diff);
      previous.current = result.paragraphs;
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load(false);
    // Смена вида документа сбрасывает базу для сравнения
    previous.current = null;
     
  }, [kind]);

  return (
    <section className="block preview">
      <h2>Предпросмотр</h2>

      <div className="toolbar">
        <select value={kind} onChange={(e) => setKind(e.target.value as DocumentKind)}>
          {DOCUMENTS.map(([value, title]) => (
            <option key={value} value={value}>
              {title}
            </option>
          ))}
        </select>
        <button type="button" className="link-button" disabled={busy} onClick={() => void load(true)}>
          Обновить и показать изменения
        </button>
        {busy && <span className="hint">Сборка…</span>}
      </div>

      <p className="hint">
        Показан текст документа, собранного из действующей версии шаблона. Оформление Word здесь не воспроизводится —
        предпросмотр нужен, чтобы увидеть, какие условные блоки сработали. Документ собирается на вашем рабочем
        месте и никуда не отправляется.
      </p>

      {error && <div className="error-box">{error}</div>}

      <div className="preview-text">
        {(diff ?? paragraphs.map((text) => ({ status: 'same', text }))).map((line, i) => (
          <p key={i} className={`preview-line ${line.status}`}>
            {line.text}
          </p>
        ))}
      </div>
    </section>
  );
}
